import React from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';

import { Modal, ModalContents, ModalTitle, ModalButton } from 'components/Modal/Modal';
import { BackButton } from 'retired/BackButtonLayout/BackButton';
import { black, primaryBlue } from 'utils/color-styles';
import { useAddCommunityUsersMutation } from 'generated/graphql';
import { Employee } from 'utils/useFetchEmployees';
import { SelectedUserList } from 'containers/CommunityPage/EmployeeSearch/SelectedUserList';

interface Props {
  communityId: string;
  onClickClose: () => void;
  goBack: () => void;
  chosenMembers: Employee[];
  removeMember: (userId: string) => void;
  invalidEmails: string[];
}

export const AddMembersReviewModal = ({
  communityId,
  onClickClose,
  chosenMembers,
  removeMember,
  invalidEmails,
  goBack,
}: Props) => {
  const chosenMemberIds = chosenMembers.map((m) => m.id);

  const [addCommunityUsers, { loading, error }] = useAddCommunityUsersMutation({
    onCompleted: () => {
      onClickClose();
    },
    variables: {
      communityId,
      userIds: chosenMemberIds,
    },
  });

  return (
    <Modal open fullWidth onClose={onClickClose}>
      <ModalContents
        onClickClose={onClickClose}
        body={
          <div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <BackButton onClick={goBack} />
              <ModalTitle style={{ marginBottom: 0, marginLeft: 12 }}>
                Add {chosenMembers.length} member{chosenMembers.length > 1 ? 's' : ''}
              </ModalTitle>
            </div>

            <Box paddingTop={4} paddingBottom={2} maxHeight={300} style={{ overflowY: 'auto' }}>
              {invalidEmails?.length > 0 && (
                <Alert
                  severity="error"
                  sx={{ '& .v5-MuiAlert-message': { marginTop: '2px' }, marginBottom: '8px' }}
                >
                  {invalidEmails.length}
                  {invalidEmails.length === 1
                    ? ' employee has invalid emails and were not selected.'
                    : ' employees have invalid emails and were not selected.'}
                </Alert>
              )}
              {error ? (
                <Alert severity="error" sx={{ '& .v5-MuiAlert-message': { marginTop: '2px' } }}>
                  An error occurred while adding users to the community. Please try again later.
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ '& .v5-MuiAlert-message': { marginTop: '2px' } }}>
                  Confirm to add {chosenMembers.length} member{chosenMembers.length > 1 ? 's' : ''}?
                  They will receive an email.
                </Alert>
              )}
            </Box>

            <SelectedUserList
              users={chosenMembers}
              removeUser={(userId): void => removeMember(userId)}
            />
          </div>
        }
        actions={
          <>
            <ModalButton
              style={{ color: primaryBlue }}
              variant="outlined"
              onClick={goBack}
              disabled={loading}
            >
              Back
            </ModalButton>
            &nbsp;
            <ModalButton
              disabled={chosenMembers.length === 0 || loading}
              // Weird one-of yellow
              style={{ backgroundColor: '#FFC475', color: black }}
              onClick={() => {
                void addCommunityUsers();
              }}
            >
              {loading ? <CircularProgress size={21} style={{ color: 'white' }} /> : 'Add'}
            </ModalButton>
          </>
        }
      />
    </Modal>
  );
};
